import { useEffect, useRef, useState } from "react";
import { Button, Card, Checkbox, Input, message, Select, Spin, Table, Typography } from "antd";
import { getProductVidName, hasSubstring, productVids, renderCheckbox } from "../../../extensions/helper";
import Product from "./product";
import { isNullOrUndef } from "../../../extensions/utils";
import { serverFetch } from "../../../server";
import { RefreshButton, ScanButton, isMobile, menuSize } from "../../../extensions/design";
import { Scan } from "../../../controls";
import ScanMobile from "@/controls/scan/scan-mobile.js";
import SportTooltip from "../../../controls/sport-tooltip/sport-tooltip";
import { BtnSettingsTable, tblGetColumns } from "../../../controls/table-settings.js/table-settings";
import { ftmPrice } from "../../../extensions/utils";

const { Text } = Typography;
const tableName = "products";

const Products = ({ filter: externalFilter }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState();
  const [filter, setFilter] = useState({ showHidden: false, searchText: "", vid: null });
  const [newBarcode, setNewBarcode] = useState('');
  const [tableKey, setTableKey] = useState(0);
  const [scanActive, setScanActive] = useState(false);
  const divRef = useRef(null);

  const getData = () => {
    setLoading(true);
    serverFetch("product")
      .then(data => {
        setItems(data);
        setLoading(false);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(getData, []);

  const baseColumns = [
    {title: "Акт.", dataIndex: "active", render: value => <Checkbox checked={value} />, width: 60},
    {title: "#", dataIndex: "id", width: 70},
    {title: "Вид", dataIndex: "vid", render: getProductVidName, width: 120},
    {title: "Наименование", dataIndex: "name", minWidth: 300},
    {title: "Единица", dataIndex: "expUnit", align:"center", width: 90},
    {title: "Цена", dataIndex: "expPrice", align:"right", width: 90},
    {title: "Длит.", dataIndex: "duration", align:"right", width: 80},
    {title: "Расср.", dataIndex: "paymentDeadline", align: "center", render: renderCheckbox, width: 80},
    {title: "Авто.", dataIndex: "autoActivationDays", align: "center", render: (value, item) => <Checkbox checked={!!value || !!item?.autoActivationOnFullPayment} />, width: 80},
    {title: "Штрихкод", dataIndex: "barcode", align:"center", width: 140},
    {title: "Календарь", dataIndex: "calendarName", align:"center", width: 150},
    {title: "Остаток", dataIndex: "storeQty", align: "right", width: 90 },
    {title: <SportTooltip title="Есть включеный пакет услуг"><div>Услуги</div></SportTooltip>, dataIndex: "hasCnt", align: "center", render: renderCheckbox, width: 80 },
    {title: "Поставщик", dataIndex: "supplierName", minWidth: 300},
  ];

  const columns = tblGetColumns(baseColumns, tableName);

  const onClose = (refresh) => {
    setEditId();
    refresh && getData();
  }

  const onRefresh = id => {
    getData();
    id && setEditId(id);
  }

  const divItem = editId != null && <Product id={editId} onClose={onClose} onRefresh={onRefresh} barcode={newBarcode} name={items.find(i => i.id === editId)?.name ?? ''}/>
  const headerSize =  divRef.current?.querySelector('.ant-table-thead')?.offsetHeight ?? 0;
  const tableHeight = divRef.current ? (document.documentElement.offsetHeight - headerSize - divRef.current.getBoundingClientRect().top - window.scrollY) : 0;
  const tableWidth = (document.documentElement.offsetWidth - divRef.current?.getBoundingClientRect().left) || 0;

  const onChangeFilter = value => setFilter({ ...filter, ...value });
  const onTableSettingsChange = () => setTableKey(prev => prev + 1);

  const effectiveFilter = isMobile
    ? { ...filter, showHidden: externalFilter?.showHidden ?? false, vid: externalFilter?.vid ?? null }
    : filter;

  const filtered = items.filter(item => {
    if (!effectiveFilter.showHidden && !item.active) return false;
    if (!hasSubstring(effectiveFilter.searchText, item.name) && !hasSubstring(effectiveFilter.searchText, item.barcode) && !hasSubstring(effectiveFilter.searchText, item.supplierName)) return false;
    if (!isNullOrUndef(effectiveFilter.vid) && item.vid !== effectiveFilter.vid) return false;
    return true;
  })

  const onScan = value => {
    setLoading(true);
    let barcode = value;
    if (barcode.length > 15 && barcode.startsWith("01"))
      barcode = barcode.slice(3, 16);
    serverFetch(`product/GetByBarcode?barcode=${barcode}`)
      .then(data => {
        setLoading(false);
        setEditId(data?.id ?? 0);
        setNewBarcode(value);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  return !isMobile ? <div>
    <div className="sport-form-title">
      <Checkbox checked={filter.showHidden} onChange={e => onChangeFilter({ showHidden: e.target.checked })}>Показывать неактивные</Checkbox>
      <Input placeholder="Поиск: наимен., штрихкод, поставщик" value={filter.searchText} onChange={e => onChangeFilter({searchText: e.target.value})} style={{maxWidth: 500}} />
      <Select options={productVids} value={filter.vid} allowClear style={{width: 200}} onChange={e => onChangeFilter({vid: e})} placeholder="Вид"/>
      <RefreshButton onClick={getData}/>
      <Button onClick={() => { setEditId(0); setNewBarcode('')}}>Новый</Button>
      <ScanButton title="Просканируйте штрихкод для поиска товара или добавления нового" onScan={onScan}/>
      <BtnSettingsTable name={tableName} columns={baseColumns} onSettingsChange={onTableSettingsChange}/>
    </div>
    <div ref={divRef} style={{width: tableWidth}}>
      <Table
        key={tableKey}
        dataSource={filtered}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
        scroll={{ x: "max-content", y: tableHeight }}
        tableLayout="auto"
        rowSelection={{ type: 'radio', selectedRowKeys: [editId], columnWidth: 32, renderCell: checked => <div className={checked ? "list-indicator" : ""} /> }}
        onRow={(record) => ({onClick: () => {setEditId(record.id)}})}
      />
    </div>
    {divItem}
    {editId == null && <Scan onScan={onScan}/>}
  </div> :
  <>
    <div className="col-one-title" style={{marginTop: "8px", marginBottom: "4px"}}>
      <Input
        placeholder="Поиск: наимен., штрихкод, поставщик"
        value={filter.searchText}
        onChange={e => onChangeFilter({ searchText: e.target.value })}
        allowClear
        className="search-str"
      />
      <img src="/images/scan.svg" style={{width: 32}} alt="scan" onClick={() => setScanActive(true)}/>
    </div>
    {scanActive && <ScanMobile onClose={() => setScanActive(false)} onScan={value => { setScanActive(false); onScan(value); }}/>}
    <Button style={{ width: "96%", marginBottom: 4, marginLeft: 8, marginRight: 8 }} onClick={() => { setEditId(0); setNewBarcode(''); }}>Добавить</Button>
    {loading ? <Spin /> : (
      <div className="sale-cards-list" style={{height: window.innerHeight - menuSize - 100, overflowY: "scroll", overflowX: "clip"}}>
        {filtered.map((item) => (
          <Card key={item.id} className="client-item-abon-card" onClick={() => setEditId(item.id)}
            style={{ borderLeft: `4px solid ${item.active ? '#52c41a' : '#d9d9d9'}` }}>
            <div className="sale-item-abon-card-name-debt-fields">
              <Text strong className="sales-product-name">{item.name}</Text>
              {item.expPrice != null && <Text strong>{ftmPrice(item.expPrice)}</Text>}
            </div>
            <div className="client-item-line-triple" style={{ lineHeight: 1.2 }}>
              <div className="client-item-clip">{getProductVidName(item.vid)}</div>
              <div></div>
              <div className="client-item-clip-right"> {item.vid == 0 ? item.expUnit : item.vid == 1 ? item.duration + " мин." : ""}</div>
            </div>
            <div>
              {item.vid === 0
                ? <Text style={{ marginTop: 0 }}>Штрихкод: {item.barcode || "не задан"}</Text>
                : <Checkbox checked={!!item.hasCnt} disabled style={{ marginTop: 0, fontSize: 12 }}>Есть услуги</Checkbox>
              }
            </div>
          </Card>
        ))}
        {filtered.length === 0 && (
          <div style={{ display: "flex", justifyContent: "center", width: "100%", marginBottom: "12px" }}>
            <Text>Нет данных</Text>
          </div>
        )}
      </div>
    )}
    {divItem}
    {editId == null && <Scan onScan={onScan}/>}
  </>
}

export default Products;
